import {
  ArrowDown,
  ArrowUp,
  Building2,
  CreditCard,
  MoreHorizontal,
  Package,
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Badge, BadgeProps } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Card, CardContent } from '@marine/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@marine/ui/dropdown-menu';
import { AIObservabilityLink } from '@/modules/platform/components/AIObservabilityLink';

interface StatItem {
  title: string;
  value: number;
  delta: number;
  lastMonth: number;
  positive: boolean;
  icon: React.ElementType;
  color: string;
  format?: (v: number) => string;
  lastFormat?: (v: number) => string;
}

const platformStats: StatItem[] = [
  {
    title: 'Active Tenants',
    value: 47,
    delta: 8.5,
    lastMonth: 43,
    positive: true,
    icon: Building2,
    color: 'text-blue-600',
  },
  {
    title: 'Total Users',
    value: 1256,
    delta: 12.3,
    lastMonth: 1118,
    positive: true,
    icon: Users,
    color: 'text-emerald-600',
  },
  {
    title: 'Monthly Revenue',
    value: 84500,
    delta: 15.2,
    lastMonth: 73350,
    positive: true,
    format: (v: number) => `$${(v / 1000).toFixed(1)}K`,
    lastFormat: (v: number) => `$${(v / 1000).toFixed(1)}K`,
    icon: CreditCard,
    color: 'text-purple-600',
  },
  {
    title: 'Active Modules',
    value: 156,
    delta: 4.1,
    lastMonth: 150,
    positive: true,
    icon: Package,
    color: 'text-amber-600',
  },
];

function formatNumber(n: number) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return n.toLocaleString();
  return n.toString();
}

// Recent tenant activity
const recentTenants = [
  { name: 'Marina Bay Boats', status: 'active', plan: 'Professional', users: 12, since: '2 days ago' },
  { name: 'Coastal Yachts Inc', status: 'active', plan: 'Enterprise', users: 28, since: '5 days ago' },
  { name: 'Harbor Marine Sales', status: 'trial', plan: 'Professional', users: 5, since: '1 week ago' },
  { name: 'Blue Water Dealers', status: 'active', plan: 'Starter', users: 3, since: '2 weeks ago' },
  { name: 'Sunset Marina LLC', status: 'suspended', plan: 'Professional', users: 8, since: '3 weeks ago' },
];

const tenantStatusStyles: Record<string, { variant: BadgeProps['variant']; icon: React.ElementType }> = {
  active: { variant: 'success', icon: CheckCircle },
  trial: { variant: 'warning', icon: Clock },
  suspended: { variant: 'destructive', icon: XCircle },
  pending: { variant: 'info', icon: AlertCircle },
};

// Subscription breakdown
const subscriptionBreakdown = [
  { plan: 'Enterprise', count: 8, percentage: 17, revenue: 31920, color: 'bg-purple-500' },
  { plan: 'Professional', count: 24, percentage: 51, revenue: 47760, color: 'bg-blue-500' },
  { plan: 'Starter', count: 12, percentage: 26, revenue: 4320, color: 'bg-emerald-500' },
  { plan: 'Trial', count: 3, percentage: 6, revenue: 0, color: 'bg-gray-400' },
];

export function PlatformDashboard() {
  return (
    <div className="p-5">
      <div className="space-y-3">
        {/* Dashboard Title */}
        <div className="mb-3">
          <h1 className="text-base font-semibold">Platform Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Overview of all tenants and platform metrics
          </p>
        </div>

        {/* Stats Grid - 4 columns */}
        <div className="grow grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {platformStats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card key={index} className="relative overflow-hidden">
                {/* Ultra compact header with inline icon */}
                <div className="flex items-start justify-between p-3 pb-2">
                  <div className="flex items-center gap-2">
                    <IconComponent className={`size-4 ${stat.color}`} />
                    <span className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 -mt-1">
                        <MoreHorizontal className="size-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" side="bottom">
                      <DropdownMenuItem className="text-xs">Settings</DropdownMenuItem>
                      <DropdownMenuItem className="text-xs">Pin to Dashboard</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant="destructive" className="text-xs">Remove</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Value and delta in single line */}
                <div className="px-3 pb-2 flex items-baseline gap-2">
                  <span className="text-2xl font-bold">
                    {stat.format
                      ? stat.format(stat.value)
                      : formatNumber(stat.value)}
                  </span>
                  <Badge
                    variant={stat.positive ? 'success' : 'destructive'}
                    appearance="light"
                    className="text-xs h-5 px-1.5"
                  >
                    {stat.delta > 0 ? <ArrowUp className="size-2.5" /> : <ArrowDown className="size-2.5" />}
                    {Math.abs(stat.delta)}%
                  </Badge>
                </div>

                {/* Comparison bar at bottom */}
                <div className="px-3 pb-3">
                  <div className="text-xs text-muted-foreground flex items-center justify-between">
                    <span>Last month</span>
                    <span className="font-semibold">
                      {stat.lastFormat
                        ? stat.lastFormat(stat.lastMonth)
                        : formatNumber(stat.lastMonth)}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Subscription Breakdown + Growth Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Subscription Plans Breakdown */}
          <Card className="p-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold">Subscription Plans</span>
              <Badge variant="secondary" appearance="light" className="text-xs">
                {subscriptionBreakdown.reduce((sum, p) => sum + p.count, 0)} total
              </Badge>
            </div>

            <div className="space-y-1.5">
              {subscriptionBreakdown.map((plan, index) => (
                <div key={index}>
                  <div className="space-y-1 py-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate flex-1 min-w-0">{plan.plan}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground tabular-nums">{plan.count}</span>
                        <span className="text-sm font-semibold tabular-nums min-w-[32px] text-right">
                          {plan.percentage}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-muted/40 rounded-full h-1">
                      <div
                        className={`h-1 rounded-full ${plan.color}`}
                        style={{ width: `${plan.percentage}%` }}
                      />
                    </div>
                  </div>
                  {index < subscriptionBreakdown.length - 1 && (
                    <div className="border-b border-border/40" />
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Platform Revenue Card */}
          <Card className="p-3 lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Platform Revenue</span>
              <Badge variant="success" appearance="light" className="text-xs">
                <TrendingUp className="size-3" />
                +15.2%
              </Badge>
            </div>

            <div className="mb-2">
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-emerald-600">$84.5K</span>
                <span className="text-xs text-muted-foreground">this month</span>
              </div>
              <div className="text-xs text-emerald-600 font-medium">+$11.2K from last month</div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <div className="space-y-0.5">
                <div className="text-xs text-muted-foreground">Enterprise</div>
                <div className="text-lg font-bold">$31.9K</div>
                <div className="text-xs text-emerald-600">+18%</div>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs text-muted-foreground">Professional</div>
                <div className="text-lg font-bold">$47.8K</div>
                <div className="text-xs text-emerald-600">+12%</div>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs text-muted-foreground">Starter</div>
                <div className="text-lg font-bold">$4.3K</div>
                <div className="text-xs text-blue-600">+8%</div>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs text-muted-foreground">MRR Growth</div>
                <div className="text-lg font-bold">15%</div>
                <div className="text-xs text-purple-600">YoY</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Observability — platform-admin-only deep-links to diagnostic surfaces
            (Phase 42 Plan 03 OBS-05). Route-gated via PlatformRoutes; never
            rendered for tenant-subdomain users. */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold">Observability</h2>
          <AIObservabilityLink />
        </section>

        {/* Recent Tenants Table */}
        <Card>
          <div className="flex items-center justify-between p-3 border-b">
            <span className="text-sm font-semibold">Recent Tenants</span>
            <Button variant="outline" size="sm" className="h-7 text-xs">
              View All
            </Button>
          </div>
          <CardContent className="p-0">
            <div className="divide-y">
              {recentTenants.map((tenant, index) => {
                const statusStyle = tenantStatusStyles[tenant.status];
                const StatusIcon = statusStyle.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2.5 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="size-8 rounded-md bg-muted flex items-center justify-center">
                        <Building2 className="size-4 text-muted-foreground" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">{tenant.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {tenant.users} users • Joined {tenant.since}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" appearance="light" className="text-xs">
                        {tenant.plan}
                      </Badge>
                      <Badge variant={statusStyle.variant} appearance="light" className="text-xs capitalize">
                        <StatusIcon className="size-3" />
                        {tenant.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
